import React from "react";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { alpha, Box, Card, Chip, Stack, Typography } from "@mui/material";
import { format } from "date-fns";
import { Link as RouterLink } from "react-router";

import type { Project } from "src/domains/project/types";
import { PROJECTS } from "src/platform/constants/routes";
import { ALPHA } from "src/shell/styles/designTokens";
import { getCardStyles, getStatusChipProps } from "src/shell/styles/themeUtils";

interface ProjectCardProps {
  project: Project & { resourceCount?: number; appCount?: number };
  orgAlias: string;
  /** Display variant: "list" (horizontal, full-width) or "grid" (vertical, 360×180). Defaults to "list". */
  variant?: "list" | "grid";
}

const metricSx = {
  minWidth: 64,
  textAlign: "center",
};

export const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  orgAlias,
  variant = "list",
}) => {
  const chipProps = getStatusChipProps(project.status ?? "active");

  const createdAtLabel = () => {
    const raw = project.createdAt;
    if (!raw) return "N/A";
    try {
      return format(new Date(raw), "MMM d, yyyy");
    } catch {
      return "N/A";
    }
  };

  const tags = project.tags ? Object.entries(project.tags) : [];
  const resources = project.resourceCount ?? 0;
  const apps = project.appCount ?? 0;
  const hasRunningResources = resources > 0;

  const avatar = (
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: (theme) =>
          alpha(theme.palette.primary.main, ALPHA.STAT_TILE_BG),
        color: "primary.main",
        flexShrink: 0,
        fontSize: "1.125rem",
        fontWeight: 700,
      }}
    >
      {project.name.charAt(0).toUpperCase()}
    </Box>
  );

  const detailPath = PROJECTS.DETAILS.getPath(orgAlias, project.alias);

  // ── Grid variant: vertical 360×180 card ──
  if (variant === "grid") {
    return (
      <Card
        component={RouterLink}
        to={detailPath}
        sx={(theme) => ({
          ...getCardStyles(theme, {
            hasShadow: true,
            hasBorder: true,
            isInteractive: true,
          }),
          p: 2.5,
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          minHeight: 180,
          borderColor: alpha(theme.palette.divider, 0.06),
          textDecoration: "none",
          borderRadius: "12px",
        })}
      >
        {/* Top row: avatar + status chip + health dot */}
        <Stack direction="row" spacing={1.5} alignItems="center">
          {avatar}
          <Chip
            label={chipProps.label}
            color={chipProps.color}
            variant={chipProps.variant}
            size="small"
            sx={{ fontWeight: 600, fontSize: "0.6875rem" }}
          />
          {hasRunningResources && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: "success.main",
                flexShrink: 0,
              }}
              aria-label="Has running resources"
            />
          )}
        </Stack>

        {/* Name + alias */}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h6"
            fontWeight={600}
            fontSize="1.125rem"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {project.name}
          </Typography>
          <Typography
            variant="body2"
            sx={(theme) => ({
              fontFamily: '"JetBrains Mono", "Roboto Mono", monospace',
              color: theme.palette.text.secondary,
              opacity: 0.8,
              fontSize: "0.8125rem",
            })}
          >
            {project.alias}
          </Typography>
        </Box>

        {/* Description (2-line clamp) */}
        {project.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            fontSize="0.8125rem"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {project.description}
          </Typography>
        )}

        {/* Spacer to push metrics + date to bottom */}
        <Box sx={{ flex: 1 }} />

        {/* Metrics row */}
        <Stack direction="row" spacing={3}>
          <Box>
            <Typography
              variant="h6"
              fontWeight={700}
              fontSize="1.5rem"
              lineHeight={1.2}
            >
              {resources}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={500}
              fontSize="0.6875rem"
            >
              resources
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="h6"
              fontWeight={700}
              fontSize="1.5rem"
              lineHeight={1.2}
            >
              {apps}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={500}
              fontSize="0.6875rem"
            >
              apps
            </Typography>
          </Box>
        </Stack>

        {/* Date + Open link */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="caption"
            color="text.secondary"
            fontSize="0.75rem"
          >
            {createdAtLabel()}
          </Typography>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={(theme) => ({
              display: "inline-flex",
              alignItems: "center",
              gap: 0.25,
              color: theme.palette.primary.main,
            })}
          >
            Open <ArrowForwardIcon fontSize="small" />
          </Typography>
        </Stack>
      </Card>
    );
  }

  // ── List variant (default): horizontal full-width card ──
  return (
    <Card
      component={RouterLink}
      to={detailPath}
      sx={(theme) => ({
        ...getCardStyles(theme, {
          hasShadow: true,
          hasBorder: true,
          isInteractive: true,
        }),
        px: 2.5,
        py: 2,
        cursor: "pointer",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 2, md: 3 },
        alignItems: { xs: "flex-start", md: "center" },
        justifyContent: "space-between",
        borderColor: alpha(theme.palette.divider, 0.08),
        textDecoration: "none",
      })}
    >
      <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
        >
          {/* Project avatar — 44×44 colored square with initial */}
          {avatar}
          <Chip
            label={chipProps.label}
            color={chipProps.color}
            variant={chipProps.variant}
            size="small"
            sx={{ fontWeight: 600, fontSize: "0.6875rem" }}
          />
          {hasRunningResources && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: "success.main",
                flexShrink: 0,
              }}
              aria-label="Has running resources"
            />
          )}
          <Typography variant="h6" fontWeight={600} fontSize="1.125rem" noWrap>
            {project.name}
          </Typography>
          <Typography
            variant="body2"
            sx={(theme) => ({
              fontFamily: '"JetBrains Mono", "Roboto Mono", monospace',
              color: theme.palette.text.secondary,
              opacity: 0.8,
            })}
          >
            {project.alias}
          </Typography>
        </Stack>

        {project.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 1,
              WebkitBoxOrient: "vertical",
            }}
          >
            {project.description}
          </Typography>
        )}

        {tags.length > 0 && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap">
            {tags.map(([key, value]) => (
              <Chip
                key={`${key}-${value}`}
                label={`${key}: ${value}`}
                size="small"
                variant="outlined"
                sx={(theme) => ({
                  borderColor: alpha(theme.palette.divider, 0.08),
                  color: "text.secondary",
                  fontWeight: 500,
                  fontSize: "0.6875rem",
                })}
              />
            ))}
          </Stack>
        )}
      </Stack>

      <Stack
        direction={{ xs: "row", md: "row" }}
        spacing={{ xs: 3, md: 4 }}
        alignItems="center"
        sx={{ flexShrink: 0 }}
      >
        <Box sx={metricSx}>
          <Typography
            variant="h4"
            fontWeight={700}
            fontSize="1.5rem"
            lineHeight={1.2}
          >
            {resources}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            resources
          </Typography>
        </Box>
        <Box sx={metricSx}>
          <Typography
            variant="h4"
            fontWeight={700}
            fontSize="1.5rem"
            lineHeight={1.2}
          >
            {apps}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            apps
          </Typography>
        </Box>
        <Box sx={{ minWidth: 100, textAlign: "right" }}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={(theme) => ({
              display: "inline-flex",
              alignItems: "center",
              gap: 0.25,
              color: theme.palette.primary.main,
            })}
          >
            Open <ArrowForwardIcon fontSize="small" />
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {createdAtLabel()}
          </Typography>
        </Box>
      </Stack>
    </Card>
  );
};

export default ProjectCard;
